import type { Health } from '@ops/contracts'
import {
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'

import { cx } from '../lib/cx'
import { Icon } from './Icon'

/**
 * Tabla genérica y tipada del panel: orden, filtro, densidad y **densidad por
 * severidad de fila**.
 *
 * La densidad por severidad es el injerto del tablero de riesgo que resuelve el
 * problema real de una tabla de 31 filas: una fila crítica **respira** (más
 * padding, fondo tintado, texto en negro) y una fila sana va **compacta y en
 * gris**. El ojo cae solo donde hay que mirar sin tener que ordenar ni filtrar
 * nada. El color es refuerzo: la severidad viaja además en la celda de estado.
 */
export interface Column<T> {
  key: string
  header: string
  /** Las columnas numéricas van a la derecha y con dígitos de ancho fijo. */
  align?: 'left' | 'right'
  /** Ancho fijo, ej. '140px'. Sin esto la columna se reparte el sobrante. */
  width?: string
  render: (row: T) => ReactNode
  /** Sin `sortValue` la columna no es ordenable (una de acciones, por ejemplo). */
  sortValue?: (row: T) => string | number | null
  /** Texto que ve el filtro. Por defecto usa `sortValue` cuando es string. */
  filterValue?: (row: T) => string
  /** true = esta columna identifica la fila: se renderiza como `th scope="row"`. */
  rowHeader?: boolean
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<Column<T>>
  rows: readonly T[]
  rowKey: (row: T) => string
  /** Severidad de la fila. Sin esto todas las filas son neutras. */
  rowSeverity?: (row: T) => Health
  /** true = la fila crítica respira y la sana va compacta y en gris. */
  densidadPorSeveridad?: boolean
  /** Densidad base de las filas sin severidad. */
  density?: 'compacta' | 'normal'
  onRowClick?: (row: T) => void
  /** Muestra el campo de filtro con este texto de ayuda. */
  filtroPlaceholder?: string
  /** Orden inicial. Sin esto respeta el orden que mandó la API. */
  ordenInicial?: { key: string; dir: 'asc' | 'desc' }
  /** Qué decir cuando no hay filas. */
  emptyMessage?: string
  /**
   * Contexto de la tabla: qué lista y con qué fuente. Va en el `caption`.
   *
   * **Obligatorio**: una tabla sin `caption` queda sin nombre accesible y un
   * lector de pantalla la anuncia como "tabla, 9 columnas" y nada más. Que lo
   * cace el compilador y no un reviewer.
   */
  caption: ReactNode
  /** Fila de totales o nota, en el `tfoot`. */
  footer?: ReactNode
  className?: string
}

type Orden = { key: string; dir: 'asc' | 'desc' } | null

const PADDING_BASE: Record<'compacta' | 'normal', string> = {
  compacta: 'px-3 py-1.5',
  normal: 'px-3 py-2',
}

/** Padding por severidad: la gravedad se mide en aire, no solo en color. */
const PADDING_SEVERIDAD: Record<Health, string> = {
  critical: 'px-3 py-3',
  warning: 'px-3 py-2',
  ok: 'px-3 py-1',
  unknown: 'px-3 py-2',
}

const TONO_FILA: Record<Health, string> = {
  critical: 'bg-danger-soft text-text',
  warning: 'bg-warning-soft/40 text-text',
  ok: 'text-muted-strong',
  unknown: 'text-text',
}

function comparar(
  a: string | number | null,
  b: string | number | null,
): number {
  // Sin dato al final en ascendente: un null arriba tapa el dato que importa.
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'es')
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowSeverity,
  densidadPorSeveridad = false,
  density = 'normal',
  onRowClick,
  filtroPlaceholder,
  ordenInicial,
  emptyMessage = 'Sin resultados',
  caption,
  footer,
  className,
}: DataTableProps<T>) {
  const [orden, setOrden] = useState<Orden>(ordenInicial ?? null)
  const [filtro, setFiltro] = useState('')
  const filtroId = useId()

  const filtradas = useMemo(() => {
    const q = filtro.trim().toLowerCase()
    if (q === '') return rows
    return rows.filter((fila) =>
      columns.some((col) => {
        const texto =
          col.filterValue !== undefined
            ? col.filterValue(fila)
            : col.sortValue !== undefined
              ? String(col.sortValue(fila) ?? '')
              : ''
        return texto.toLowerCase().includes(q)
      }),
    )
  }, [rows, columns, filtro])

  const filas = useMemo(() => {
    if (orden === null) return filtradas
    const columna = columns.find((col) => col.key === orden.key)
    const sortValue = columna?.sortValue
    if (sortValue === undefined) return filtradas

    const ordenadas = [...filtradas].sort((a, b) =>
      comparar(sortValue(a), sortValue(b)),
    )
    return orden.dir === 'asc' ? ordenadas : ordenadas.reverse()
  }, [filtradas, columns, orden])

  /** asc → desc → sin orden. El tercer click devuelve el orden que mandó la API,
   *  que muchas veces ya es el relevante (por riesgo, por antigüedad). */
  function alternarOrden(key: string) {
    setOrden((actual) => {
      if (actual === null || actual.key !== key) return { key, dir: 'asc' }
      if (actual.dir === 'asc') return { key, dir: 'desc' }
      return null
    })
  }

  /**
   * Una fila clickeable no puede tragarse el click de lo que tiene adentro.
   *
   * Sin esta guarda, clickear el deep link a Close abría la pestaña **y** el
   * drawer del cliente: el usuario se iba al CRM y volvía a un panel con una
   * ficha abierta que nunca pidió. Vale igual para los botones ("Vincular",
   * "Cargalo acá") y para los campos de una celda editable.
   */
  function esControlPropio(destino: EventTarget | null): boolean {
    return (
      destino instanceof Element &&
      destino.closest('a, button, input, select, textarea, [role="button"]') !==
        null
    )
  }

  function onFilaClick(evento: MouseEvent<HTMLTableRowElement>, fila: T) {
    if (onRowClick === undefined) return
    if (esControlPropio(evento.target)) return
    onRowClick(fila)
  }

  function onFilaKeyDown(evento: KeyboardEvent<HTMLTableRowElement>, fila: T) {
    if (onRowClick === undefined) return
    if (evento.key !== 'Enter' && evento.key !== ' ') return
    // Enter sobre un link enfocado adentro de la fila activa el link, no la
    // fila: el keydown burbujea hasta el `<tr>` igual que el click.
    if (esControlPropio(evento.target)) return
    evento.preventDefault()
    onRowClick(fila)
  }

  function paddingDe(severidad: Health | undefined): string {
    if (densidadPorSeveridad && severidad !== undefined) {
      return PADDING_SEVERIDAD[severidad]
    }
    return PADDING_BASE[density]
  }

  return (
    <div className={cx('min-w-0', className)}>
      {filtroPlaceholder !== undefined ? (
        <div className="mb-2 flex items-center gap-2">
          <label htmlFor={filtroId} className="rotulo">
            Filtrar
          </label>
          <span className="relative inline-flex items-center">
            <Icon
              name="buscar"
              size={12}
              className="pointer-events-none absolute left-2 text-muted"
            />
            <input
              id={filtroId}
              type="search"
              value={filtro}
              placeholder={filtroPlaceholder}
              onChange={(evento) => setFiltro(evento.target.value)}
              className="h-7 w-64 rounded-md border border-border bg-surface pl-7 pr-2 text-meta text-text placeholder:text-muted hover:border-n300"
            />
          </span>
        </div>
      ) : null}

      <div
        data-testid="data-table-scroller"
        className="relative w-full overflow-x-auto rounded-card border border-border bg-surface"
      >
        {/* Sin `aria-describedby`: el `<caption>` ya nombra la tabla, y
            apuntarle desde la propia tabla hace que algunos lectores lo lean
            dos veces. */}
        <table className="w-full border-collapse text-meta">
          <caption className="caption-top px-3 py-2 text-left text-micro text-muted-strong">
            {caption}
          </caption>
          <thead>
            <tr className="border-b border-border bg-n100">
              {columns.map((col) => {
                const activa = orden !== null && orden.key === col.key
                const ordenable = col.sortValue !== undefined

                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={col.width !== undefined ? { width: col.width } : undefined}
                    aria-sort={
                      ordenable
                        ? activa
                          ? orden.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                        : undefined
                    }
                    className={cx(
                      'rotulo whitespace-nowrap',
                      PADDING_BASE.compacta,
                      col.align === 'right' ? 'text-right' : 'text-left',
                      // La columna ordenada no se distingue solo por la forma
                      // de un glifo de 10px: queda subrayada por un borde de
                      // 2px, y el rótulo pasa a negro (en el botón, porque
                      // `.rotulo` gana por orden de fuente sobre el `<th>`).
                      activa && 'border-b-2 border-text',
                    )}
                  >
                    {ordenable ? (
                      <button
                        type="button"
                        onClick={() => alternarOrden(col.key)}
                        className={cx(
                          'inline-flex items-center gap-1 uppercase transition-colors duration-150 ease-brand hover:text-text',
                          activa && 'text-text',
                        )}
                      >
                        {col.header}
                        {/* La flecha es decorativa: el estado real lo lee un
                            lector de pantalla desde el aria-sort del th. Pero
                            decorativa no es invisible: en text-n300 sobre el
                            thead daba 1,36:1, muy por debajo del 3:1 de un
                            indicador no textual. */}
                        <Icon
                          name={
                            activa
                              ? orden.dir === 'asc'
                                ? 'orden-asc'
                                : 'orden-desc'
                              : 'orden'
                          }
                          size={10}
                          className={activa ? 'text-text' : 'text-muted-strong'}
                        />
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {filas.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={cx(
                    PADDING_BASE.normal,
                    'text-center text-muted-strong',
                  )}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              filas.map((fila) => {
                const severidad = rowSeverity?.(fila)
                const padding = paddingDe(severidad)

                return (
                  <tr
                    key={rowKey(fila)}
                    data-testid="data-table-row"
                    data-severidad={severidad}
                    onClick={
                      onRowClick !== undefined
                        ? (evento) => onFilaClick(evento, fila)
                        : undefined
                    }
                    onKeyDown={(evento) => onFilaKeyDown(evento, fila)}
                    tabIndex={onRowClick !== undefined ? 0 : undefined}
                    className={cx(
                      'border-b border-border last:border-b-0',
                      densidadPorSeveridad && severidad !== undefined
                        ? TONO_FILA[severidad]
                        : undefined,
                      onRowClick !== undefined &&
                        'cursor-pointer transition-colors duration-150 ease-brand hover:bg-n100',
                    )}
                  >
                    {columns.map((col) => {
                      const clases = cx(
                        padding,
                        col.align === 'right'
                          ? 'tabular text-right'
                          : 'text-left',
                      )
                      return col.rowHeader === true ? (
                        <th
                          key={col.key}
                          scope="row"
                          className={cx(
                            clases,
                            'font-medium normal-case tracking-normal text-text',
                          )}
                        >
                          {col.render(fila)}
                        </th>
                      ) : (
                        <td key={col.key} className={clases}>
                          {col.render(fila)}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>

          {footer !== undefined ? (
            <tfoot>
              <tr className="border-t border-border bg-n100">
                <td
                  colSpan={columns.length}
                  className={cx(PADDING_BASE.compacta, 'text-muted-strong')}
                >
                  {footer}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  )
}
